import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CollaboratorShareBreeding,
  CollaboratorShareResourceType,
  fetchCollaboratorShares,
  setCollaboratorShare,
} from "../../../api/collaborator";
import { AccountWithoutPassword } from "../../../generated/types";
import { Button } from "../../common/Button";
import { Modal, ModalActions } from "../../common/Modal";

type ShareRowProps = {
  label: string;
  kind: string;
  checked: boolean;
  disabled: boolean;
  indented: boolean;
  onChange: () => void;
};

const ShareRow = ({
  label,
  kind,
  checked,
  disabled,
  indented,
  onChange,
}: ShareRowProps) => (
  <label
    className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${
      checked
        ? "border-secondary bg-grey-light"
        : "border-grey-medium hover:bg-grey-light"
    } ${indented ? "ml-5" : ""} ${
      disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className="w-4 h-4 accent-secondary cursor-pointer"
    />
    <span
      className={`truncate text-default-text ${
        indented ? "" : "font-oxaniumMedium"
      }`}
    >
      {label}
    </span>
    <span className="ml-auto text-xs text-tertiary shrink-0">{kind}</span>
  </label>
);

/**
 * Partage, en consultation seule, d'élevages entiers ou de bâtiments avec un collaborateur.
 * Chaque case enregistre aussitôt, comme le partage avec un partenaire.
 */
export const CollaboratorShareModal = ({
  account,
  onClose,
}: {
  account: AccountWithoutPassword;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [breedings, setBreedings] = useState<CollaboratorShareBreeding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const result = await fetchCollaboratorShares(account.id);
    setBreedings(result.breedings);
    setError(result.error && t(result.error));
    setLoading(false);
  }, [account.id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (
    resourceType: CollaboratorShareResourceType,
    id: number,
    shared: boolean
  ) => {
    setBusy(true);
    setError("");
    const result = await setCollaboratorShare(
      account.id,
      resourceType,
      id,
      !shared
    );
    if (result) setError(t(result));
    // Rechargement plutôt que mise à jour locale : partager un élevage change l'état
    // affiché de tous ses bâtiments.
    await load();
    setBusy(false);
  };

  const name = `${account.first_name} ${account.last_name}`;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t("collaborator-share-title", { name })}
      icon="share-nodes"
      maxWidth="lg"
    >
      <div className="py-2">
        <p className="text-tertiary mb-4">
          {t("collaborator-share-text", { name })}
        </p>

        {error && (
          <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-tertiary">{t("loading")}</p>
        ) : breedings.length === 0 ? (
          !error && (
            <p className="py-6 text-center text-tertiary">
              {t("collaborator-share-empty")}
            </p>
          )
        ) : (
          <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
            {breedings.map((breeding) => (
              <div key={breeding.id} className="flex flex-col gap-1">
                <ShareRow
                  label={breeding.name}
                  kind={t("breeding")}
                  checked={breeding.shared}
                  disabled={busy}
                  indented={false}
                  onChange={() =>
                    handleToggle("breeding", breeding.id, breeding.shared)
                  }
                />
                {breeding.buildings.map((building) => (
                  <ShareRow
                    key={building.id}
                    label={building.name}
                    kind={t("building")}
                    // Couvert par l'élevage : le décocher n'aurait aucun effet.
                    checked={building.shared || breeding.shared}
                    disabled={busy || breeding.shared}
                    indented
                    onChange={() =>
                      handleToggle("building", building.id, building.shared)
                    }
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        <ModalActions>
          <Button onClick={onClose}>{t("button-close")}</Button>
        </ModalActions>
      </div>
    </Modal>
  );
};
